import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, CheckCircle2, Clock, XCircle, Landmark, ArrowRight } from "lucide-react";

const STATUS_META = {
  pending_transfer: {
    label: "Awaiting Your Transfer",
    tone: "text-[#C8A96A] border-[#C8A96A]",
    icon: <Clock className="text-[#C8A96A]" size={40} />,
    kicker: "Order Placed",
  },
  pending_verification: {
    label: "Verifying Your Payment",
    tone: "text-[#C8A96A] border-[#C8A96A]",
    icon: <Clock className="text-[#C8A96A] float-slow" size={40} />,
    kicker: "Payment Sent",
  },
  paid: {
    label: "Payment Confirmed",
    tone: "text-[#7FB77E] border-[#7FB77E]",
    icon: <CheckCircle2 className="text-[#7FB77E]" size={40} />,
    kicker: "Match Won",
  },
  cancelled: {
    label: "Order Cancelled",
    tone: "text-[#8B4530] border-[#8B4530]",
    icon: <XCircle className="text-[#8B4530]" size={40} />,
    kicker: "Cancelled",
  },
};

function CopyRow({ label, value, testid }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <div className="flex items-center justify-between border-b border-[#332D2B] py-3">
      <div>
        <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">{label}</div>
        <div data-testid={testid} className="font-display text-2xl text-[#F2EBDD] mt-1 tracking-wide">{value}</div>
      </div>
      <button
        onClick={copy}
        data-testid={`${testid}-copy`}
        className="font-mono-retro text-[10px] tracking-[0.25em] uppercase text-[#A8A39D] hover:text-[#C8A96A] inline-flex items-center gap-2 border border-[#332D2B] px-3 py-2"
      >
        <Copy size={12} /> Copy
      </button>
    </div>
  );
}

export default function OrderConfirmation() {
  const { reference } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/orders/reference/${reference}`);
      setOrder(r.data);
    } catch {
      toast.error("Order not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [reference]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmSent = async () => {
    setConfirming(true);
    try {
      const r = await api.post(`/orders/reference/${reference}/confirm-sent`);
      setOrder((prev) => ({ ...prev, ...r.data }));
      toast.success("Thanks — we'll verify your transfer shortly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update order");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-6 py-24 font-mono-retro text-[#A8A39D] uppercase tracking-[0.2em]">Loading order…</div>;
  }
  if (!order) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <XCircle className="text-[#8B4530] mx-auto" size={56} />
        <h1 className="font-display text-4xl text-[#F2EBDD] mt-4">Order Not Found</h1>
        <p className="text-[#A8A39D] mt-3">The reference <span className="text-[#F2EBDD] font-mono-retro">{reference}</span> does not match any order.</p>
        <Link to="/" className="btn-secondary mt-6 inline-flex">Back Home</Link>
      </div>
    );
  }

  const meta = STATUS_META[order.status] || STATUS_META.pending_transfer;
  const bank = order.bank_details || {};
  const amount = Number(order.amount || 0).toFixed(2);

  return (
    <div data-testid="order-confirmation" className="max-w-3xl mx-auto px-6 py-16">
      <div className="border-2 border-[#332D2B] bg-[#1C1816] p-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono-retro text-[10px] tracking-[0.4em] text-[#C8A96A] uppercase">{meta.kicker}</div>
            <h1 className="font-display text-5xl text-[#F2EBDD] mt-1 leading-tight">{meta.label}</h1>
            <p className="text-[#A8A39D] mt-3 max-w-xl">
              {order.status === "pending_transfer" && "Complete the bank transfer below to secure your order."}
              {order.status === "pending_verification" && "Thanks — we're checking with the bank. You'll get an email once confirmed."}
              {order.status === "paid" && "Payment confirmed. Your order is being prepared."}
              {order.status === "cancelled" && "This order has been cancelled by the admin."}
            </p>
          </div>
          <div className="shrink-0">{meta.icon}</div>
        </div>

        <div className="perforated my-7" />

        {/* Order summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">Order Reference</div>
            <div data-testid="order-reference" className="font-display text-3xl text-[#C8A96A] tracking-[0.15em] mt-1">{order.order_reference}</div>
            <div className="text-[11px] text-[#756F6B] mt-2 uppercase font-mono-retro tracking-[0.2em]">
              Use this as the payment reference
            </div>
          </div>
          <div>
            <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">Amount Due</div>
            <div data-testid="order-amount" className="font-display text-3xl text-[#F2EBDD] mt-1">£{amount}</div>
            <div className="text-[11px] text-[#756F6B] mt-2">{order.item_name}</div>
          </div>
        </div>

        {/* Bank details */}
        {(order.status === "pending_transfer" || order.status === "pending_verification") && (
          <div className="mt-8 border border-[#332D2B] bg-[#0F0C0B] p-6">
            <div className="flex items-center gap-3 mb-4">
              <Landmark className="text-[#C8A96A]" size={18} />
              <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase">Bank Transfer Details</div>
            </div>
            <CopyRow label="Payee Name" value={bank.payee_name} testid="bank-payee" />
            <CopyRow label="Sort Code" value={bank.sort_code} testid="bank-sort" />
            <CopyRow label="Account Number" value={bank.account_number} testid="bank-account" />
            <CopyRow label="Reference" value={order.order_reference} testid="bank-reference" />
            <CopyRow label="Amount" value={`£${amount}`} testid="bank-amount" />

            <div className="mt-5 border border-[#332D2B] bg-[#1C1816]/70 px-4 py-3">
              <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase mb-2">How it works</div>
              <ol className="text-[13px] text-[#A8A39D] leading-relaxed list-decimal ml-5 space-y-1">
                <li>Open your banking app and send <span className="text-[#F2EBDD]">£{amount}</span> to the account above.</li>
                <li>Paste <span className="text-[#F2EBDD] font-mono-retro">{order.order_reference}</span> into the payment reference field.</li>
                <li>Tap <span className="text-[#C8A96A]">&quot;I&apos;ve Sent the Payment&quot;</span> below so we can match &amp; verify your transfer.</li>
                <li>You&apos;ll get an email once the funds land — usually within a few hours.</li>
              </ol>
            </div>
          </div>
        )}

        {order.status === "pending_transfer" && (
          <button
            data-testid="confirm-sent-btn"
            onClick={confirmSent}
            disabled={confirming}
            className="btn-primary w-full justify-center mt-6"
          >
            {confirming ? "Updating…" : "I've Sent the Payment"} <ArrowRight size={16} />
          </button>
        )}

        {order.status === "pending_verification" && (
          <div data-testid="verification-note" className="mt-6 border border-[#332D2B] bg-[#0F0C0B] px-5 py-4 text-center">
            <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase">Awaiting Verification</div>
            <p className="text-[#A8A39D] text-sm mt-2">
              We&apos;ve been notified. Once we spot your transfer in the account, we&apos;ll mark this order as paid and start preparing it.
            </p>
          </div>
        )}

        {order.status === "paid" && (
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/shop" className="btn-secondary">Back to Shop</Link>
            <Link to="/raffles" className="btn-primary">More Raffles</Link>
          </div>
        )}

        {/* Customer details */}
        <div className="perforated my-7" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
          <div>
            <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase mb-2">Customer</div>
            <div className="text-[#F2EBDD]">{order.customer_name}</div>
            <div className="text-[#A8A39D]">{order.customer_email}</div>
          </div>
          {order.item_type === "jersey" && order.shipping_address && (
            <div>
              <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase mb-2">Ship To</div>
              <div className="text-[#F2EBDD] whitespace-pre-line">{order.shipping_address}</div>
              <div className="text-[11px] text-[#756F6B] mt-2 font-mono-retro tracking-[0.2em] uppercase">Est. delivery 10–17 days from confirmation</div>
            </div>
          )}
        </div>
      </div>

      <div className="text-center mt-6 font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">
        Bookmark this page · You can return anytime to /order/{order.order_reference}
      </div>
    </div>
  );
}
